import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Button } from '@mui/material';
import {
  Language as LanguageIcon,
  PhoneIphone as PhoneIphoneIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';
import PublicHeader from '../components/PublicHeader';
import FloatingConsultButton from '../components/FloatingConsultButton';
import RegisterConsultDialog from '../components/RegisterConsultDialog';
import AppShowcase from '../components/AppShowcase';
import bgImage from '../assets/images/login-bg.jpg';

const GRADIENT = 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)';

// نشان‌های ویژگی (تحت وب / موبایل / مقاوم در قطعی)
const BADGES = [
  { icon: <LanguageIcon />, label: 'کاملاً تحت وب' },
  { icon: <PhoneIphoneIcon />, label: 'موبایل‌فرندلی' },
  { icon: <CloudIcon />, label: 'پایدار در قطعی اینترنت بین‌الملل' },
];

// متن‌های توصیفی مراحل برای سئو (قابل ویرایش)
const STAGES_SEO = [
  {
    title: 'پیش‌سفارش',
    text: 'در مرحلهٔ پیش‌سفارش می‌توانید مشتری جدید ثبت کنید یا از اطلاعات مشتریان قبلی استفاده کنید و درخواست اولیهٔ مشتری را به‌دقت ثبت نمایید. این مرحله پایهٔ یک سفارش منظم و قابل‌پیگیری در نرم‌افزار حسابداری گالری پرده است.',
  },
  {
    title: 'اندازه‌گیری',
    text: 'ثبت دقیق اندازه‌ها همراه با انتخاب نوع پارچه و امکان افزودن چند اندازه‌گیری برای یک سفارش، تا هیچ جزئیاتی از کار مشتری از قلم نیفتد.',
  },
  {
    title: 'ثبت فاکتور و حسابداری',
    text: 'فاکتور را سریع و راحت صادر کنید؛ اقلام پرکاربرد از قبل آماده‌اند، محاسبات و حسابداری گالری پرده به‌صورت خودکار انجام می‌شود و خروجی نهایی یک فاکتور کامل و قابل چاپ به‌صورت PDF است.',
  },
  {
    title: 'انتخاب خیاط',
    text: 'سفارش را به خیاط شخصی خود ارجاع دهید؛ دستور کار و اندازه‌گیری‌های سفارش به‌صورت یک فایل PDF آمادهٔ چاپ برای خیاط در اختیار قرار می‌گیرد.',
  },
  {
    title: 'انتخاب نصاب',
    text: 'اطلاعات سفارش را برای نصاب ارسال کنید تا مرحلهٔ نصب نیز منظم و هماهنگ پیش برود.',
  },
  {
    title: 'پیگیری سفارش',
    text: 'وضعیت هر سفارش در تمام مراحل، از پیش‌سفارش تا نصب نهایی، شفاف و قابل پیگیری است تا همیشه از آخرین وضعیت کار باخبر باشید.',
  },
];

const AppPreviewPage = () => {
  const [consultOpen, setConsultOpen] = useState(false);
  const openConsult = () => setConsultOpen(true);

  // عنوان صفحه برای سئو
  useEffect(() => {
    const prev = document.title;
    document.title = 'نرم‌افزار حسابداری و مدیریت گالری پرده | تحت وب و موبایل';
    return () => { document.title = prev; };
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <PublicHeader onOpenConsult={openConsult} />

      {/* بخش معرفی - سئو */}
      <Box sx={{
        position: 'relative', py: { xs: 6, md: 8 }, px: 2, textAlign: 'center',
        backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(13, 22, 66, 0.7)' }} />
        <Box sx={{ position: 'relative', color: 'white', maxWidth: 860, mx: 'auto' }}>
          <Typography variant="h3" component="h1"
            sx={{ fontWeight: 'bold', mb: 2, fontSize: { xs: '1.8rem', md: '2.6rem' } }}>
            نرم‌افزار حسابداری و مدیریت گالری پرده
          </Typography>
          <Typography variant="h6" component="p" sx={{ opacity: 0.92, fontWeight: 'normal', lineHeight: 2 }}>
            از ثبت مشتری و پیش‌سفارش تا اندازه‌گیری، صدور فاکتور، ارجاع به خیاط و نصاب و پیگیری سفارش؛
            همراه با حسابداری و گزارش فروش، در یک سامانهٔ تحت وب، فارسی و موبایل‌فرندلی.
          </Typography>
        </Box>
      </Box>

      {/* شوکیس مرحله‌به‌مرحله */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <AppShowcase />
      </Container>

      {/* توضیحات متنی برای سئو */}
      <Container maxWidth="md" sx={{ pb: { xs: 6, md: 8 } }}>
        {/* باکس معرفی: تحت وب و موبایل (قبل از مراحل) */}
        <Paper elevation={0} sx={{
          p: { xs: 3, md: 4 }, mb: 5, borderRadius: 3,
          bgcolor: '#eef0f9', borderRight: '4px solid #ffd54f',
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1.5 }}>
            حسابداری گالری پرده، تحت وب و همیشه در دسترس
          </Typography>
          <Typography component="p" color="text.secondary" sx={{ lineHeight: 2.1, mb: 2.5 }}>
            نرم‌افزار حسابداری و مدیریت گالری پرده کاملاً <strong>تحت وب</strong> است؛ بدون نصب و تنها با یک
            اتصال اینترنتی، از هر نقطهٔ کشور به آن دسترسی دارید و حتی هنگام <strong>قطعی اینترنت بین‌الملل</strong>،
            روی شبکهٔ داخلی پایدار می‌ماند. رابط کاربری کاملاً واکنش‌گرا و <strong>موبایل‌فرندلی</strong> آن یعنی
            با گوشی، تبلت یا رایانه به‌سادگی کار می‌کنید و حساب‌وکتاب و سفارش‌های فروشگاه‌تان همیشه همراهتان است.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {BADGES.map((b) => (
              <Box key={b.label} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1.5, py: 0.75, borderRadius: 2,
                bgcolor: 'rgba(26,35,126,0.08)', color: '#1a237e', fontWeight: 'bold', fontSize: 14,
              }}>
                {b.icon}
                {b.label}
              </Box>
            ))}
          </Box>
        </Paper>

        {/* دکمهٔ ثبت‌نام و مشاوره (باز کردن پاپ‌اپ فرم) */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Button
            variant="contained"
            size="large"
            onClick={openConsult}
            sx={{
              bgcolor: '#ffd54f', color: '#1a237e', fontWeight: 'bold',
              px: 5, py: 1.25, fontSize: '1.05rem',
              '&:hover': { bgcolor: '#ffca28' },
            }}
          >
            ثبت‌نام و مشاوره
          </Button>
        </Box>

        <Typography variant="h4" component="h2"
          sx={{ textAlign: 'center', fontWeight: 'bold', color: '#1a237e', mb: 4 }}>
          امکانات نرم‌افزار حسابداری گالری پرده
        </Typography>
        {STAGES_SEO.map((s) => (
          <Box key={s.title} sx={{ mb: 3 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 0.5 }}>
              {s.title}
            </Typography>
            <Typography component="p" color="text.secondary" sx={{ lineHeight: 2.1 }}>
              {s.text}
            </Typography>
          </Box>
        ))}
      </Container>

      {/* فوتر */}
      <Box sx={{ background: GRADIENT, color: 'white', py: 3, textAlign: 'center' }}>
        <Typography variant="body2">© محمد نشیبی - ۱۴۰۴</Typography>
      </Box>

      <FloatingConsultButton onClick={openConsult} />
      <RegisterConsultDialog open={consultOpen} onClose={() => setConsultOpen(false)} />
    </Box>
  );
};

export default AppPreviewPage;